import {Draft} from 'immer';
import isUndefined from 'lodash/isUndefined';
import stringify from 'json-stable-stringify';
import {v4 as uuidv4} from 'uuid';

import {isBehaviorShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {LocalizationStore} from 'common/i18n/store';

import {ShowClause} from '../util/types';
import {getClauseFromBehaviorModel, getClauseFromMetricModel, maybeConvertClauseToSimpleBehavior} from './helpers';
import {getMetricLabel} from './utils/metric-names';
import {isSaveableBehavior} from './utils/behaviors';
import {getSerializedBehaviorDefinition, getSerializedClause} from './utils/bookmark';

/**
 * Expands metrics defined only by a reference
 *
 * @example {type: "metric", id: 1}
 * @example {type: "formula", id: 2}
 */
function expandCanonicalMetric<T extends ShowClause>(clause: T, {metrics}: {metrics: MetricsResource}): T {
  const model = clause.id ? metrics.getOne(clause.id)?.value : null;

  /** hydrate the metric if it hasn't been hydrated already */
  const isCanonicalMetric =
    clause.id &&
    ((clause.type === BookmarkTypes.TopLevelMetricType.Metric && isUndefined(clause.behavior)) ||
      (clause.type === BookmarkTypes.TopLevelMetricType.Formula && isUndefined(clause.definition)));

  if (isCanonicalMetric && model) {
    return getClauseFromMetricModel(model) as T;
  }

  return clause;
}

/**
 * Expands behaviors defined only by a reference
 *
 * @example {behavior: {id: 1, type: "funnel"}
 * @example {behavior: {id: 2, type: "simple"}
 */
function expandCanonicalBehavior(
  clause: BookmarkTypes.BehaviorShowClause,
  {behaviors}: {behaviors: BehaviorsResource},
) {
  const behavior = clause.behavior!;

  if (!isSaveableBehavior(behavior)) {
    return clause;
  }

  const model = behavior.id ? behaviors.getOne(behavior.id)?.value : null;

  if (!model) {
    return clause;
  }

  const modelClause = getClauseFromBehaviorModel({behavior: model, clause});

  // expands a canonical behavior definition like {behavior: {id: 1, type: "funnel"}}
  if (behavior.id && behavior.type && `behaviors` in behavior === false) {
    return modelClause;
  }

  return clause;
}

function hydrateClause(
  clause: ShowClause,
  {behaviors, metrics}: {behaviors: BehaviorsResource; metrics: MetricsResource},
) {
  clause = expandCanonicalMetric(clause, {metrics});

  if (isBehaviorShowClause(clause)) {
    clause = expandCanonicalBehavior(clause, {behaviors});
  } else if (isFormulaShowClause(clause) && clause.referencedMetrics) {
    clause.referencedMetrics = clause.referencedMetrics.map(
      (metric) => hydrateClause(metric, {behaviors, metrics}) as BookmarkTypes.BehaviorShowClause,
    );
  }

  return clause;
}

function syncMetric(
  clause: ShowClause,
  {
    previousClause,
    metrics,
    behaviors,
    i18n,
    isFirstClassMetricsEnabled,
  }: {
    previousClause?: ShowClause;
    metrics: MetricsResource;
    behaviors: BehaviorsResource;
    i18n: LocalizationStore;
    isFirstClassMetricsEnabled?: boolean;
  },
): ShowClause {
  if (isBehaviorShowClause(clause)) {
    clause = syncBehavior(clause, {previousClause: previousClause as BookmarkTypes.BehaviorShowClause, behaviors});
  }

  /** populate automatic metric names */
  if (!clause.id && isFirstClassMetricsEnabled) {
    clause = updateMetricName(clause, {i18n});
  }

  const model = clause.id ? metrics.getOne(clause.id)?.value : null;
  if (!model) {
    return clause;
  }

  const modelClause = hydrateClause(
    getClauseFromMetricModel(model, {convertEventsToSimpleBehaviors: isFirstClassMetricsEnabled}),
    {
      behaviors,
      metrics,
    },
  );

  /** determines if the metric has diverged from the model definition  */
  const hasUnsavedChanges = isModifiedMetric(clause, modelClause);

  /** determines if user action modified the metric, this compares the metric state with its previous state */
  const hasChanged = previousClause ? isModifiedMetric(clause, previousClause) : false;

  if (!clause.hasUnsavedChanges && hasUnsavedChanges && !hasChanged) {
    return hydrateClause(modelClause, {metrics, behaviors});
  }

  clause.hasUnsavedChanges = hasUnsavedChanges;

  return clause;
}

/**
 * Applies automatic naming to a given metric.
 *
 * Some examples of metric names include: Uniques of Purchase, Total Sessions of Add To Cart
 */
function updateMetricName(
  clause: ShowClause,
  {
    i18n,
  }: {
    i18n: LocalizationStore;
  },
): ShowClause {
  if (clause.id || !isBehaviorShowClause(clause) || clause.userNamed) {
    return clause;
  }

  clause.name = i18n.print(getMetricLabel(clause));

  return clause;
}

function syncBehavior(
  clause: BookmarkTypes.BehaviorShowClause,
  {previousClause, behaviors}: {previousClause?: BookmarkTypes.BehaviorShowClause; behaviors: BehaviorsResource},
): BookmarkTypes.BehaviorShowClause {
  const behavior = clause.behavior!;
  const model = behavior.id ? behaviors.getOne(behavior.id)?.value : null;

  if (!model) {
    return clause;
  }

  const modelClause = getClauseFromBehaviorModel({behavior: model, clause});

  /** determines if the behavior has diverged from the model definition  */
  const hasUnsavedChanges = isModifiedBehavior(behavior, modelClause.behavior!);

  /** determines if user action modified the behavior, this compares the behavior state with its previous state */
  const hasChanged = previousClause ? isModifiedBehavior(behavior, previousClause.behavior!) : false;

  if (!behavior.hasUnsavedChanges && hasUnsavedChanges && !hasChanged) {
    return modelClause;
  }

  clause.behavior!.hasUnsavedChanges = hasUnsavedChanges;

  return clause;
}

/**
 * Synchronizes metrics across a bookmark by updating clauses with the latest data from resources.
 *
 * This function performs two main synchronization operations:
 * 1. Updates behavior clauses with the latest behavior definitions from the behaviors resource
 * 2. Updates metric clauses with the latest metric data from the metrics resource
 *
 * For formula clauses, it also recursively updates any referenced metrics.
 *
 * @param options - The synchronization options
 * @param options.params - The bookmark parameters to update (as a Draft)
 * @param options.behaviors - The behaviors resource containing the latest behavior definitions
 * @param options.metrics - The metrics resource containing the latest metric definitions
 *
 * @returns void - The function updates the params object in-place via Immer's Draft
 */
export function syncMetricsAcrossBookmark({
  params,
  previousParams,
  behaviors,
  metrics,
  i18n,
  isFirstClassMetricsEnabled,
}: {
  params: Draft<BookmarkTypes.InsightsBookmarkParams>;
  previousParams?: Draft<BookmarkTypes.InsightsBookmarkParams> | null;
  behaviors: BehaviorsResource;
  metrics: MetricsResource;
  i18n: LocalizationStore;
  isFirstClassMetricsEnabled?: boolean;
}) {
  params.sections.show = params.sections.show.map((clause) => {
    const previousClause = previousParams?.sections.show.find((entry) => entry._idx === clause._idx);

    if (isFirstClassMetricsEnabled && isBehaviorShowClause(clause)) {
      clause = maybeConvertClauseToSimpleBehavior(clause);
    }

    /** makes sure all clauses in the bookmark have hydrated fields */
    clause = hydrateClause(clause, {metrics, behaviors});

    /** updates all referenced metrics in formulas using the metric definition  */
    if (isFormulaShowClause(clause) && clause.referencedMetrics) {
      clause.referencedMetrics = clause.referencedMetrics.map((metric) => {
        /** finds the matching referenced metric in the previous state for the formula */
        const referencedMetricPreviousClause = (
          previousClause as BookmarkTypes.FormulaShowClause
        )?.referencedMetrics?.find((sub) => sub._idx === metric._idx);

        return syncMetric(metric, {
          previousClause: referencedMetricPreviousClause,
          metrics,
          behaviors,
          i18n,
          isFirstClassMetricsEnabled,
        });
      }) as Array<BookmarkTypes.BehaviorShowClause>;
    }

    clause = syncMetric(clause, {
      previousClause,
      metrics,
      behaviors,
      i18n,
      isFirstClassMetricsEnabled,
    });

    return clause;
  });

  return assignMetricIds(params);
}

/**
 * Compares a ShowClause with a Metric to determine if they are equivalent.
 *
 * This function converts the target Metric to a ShowClause and then compares
 * it with the source ShowClause, ignoring certain keys that are not relevant
 * for the comparison (like UI state properties).
 *
 * @param source - The source ShowClause to compare
 * @param target - The target Metric to compare against
 * @returns {boolean} - Returns true if the clauses are equivalent (ignoring specific keys), false otherwise
 */
export function isModifiedMetric(source: ShowClause, target: ShowClause): boolean {
  if (!target) {
    return false;
  }

  return (
    stringify(getSerializedClause(source, {forComparison: true})) !==
    stringify(getSerializedClause(target, {forComparison: true}))
  );
}

/**
 * Compares @type{BookmarkTypes.Behavior}s to determine if they are equivalent.
 * This comparison ignores certain keys that are not relevant for the comparison like UI state properties.
 *
 * @param source - The source behavior object from BookmarkTypes
 * @param target - The behavior to compare against
 * @returns {boolean} - Returns true if the clauses are equivalent (ignoring specific keys), false otherwise
 */
export function isModifiedBehavior(source: BookmarkTypes.Behavior, target: BookmarkTypes.Behavior): boolean {
  return (
    stringify(getSerializedBehaviorDefinition(source, {forComparison: true})) !==
    stringify(getSerializedBehaviorDefinition(target, {forComparison: true}))
  );
}

/**
 * Assigns unique identifiers to all metrics in an insights bookmark's show clauses.
 *
 * This function ensures each show clause has an `_idx` property by either preserving
 * the existing ID or generating a new UUID. For formula show clauses, it also
 * recursively assigns IDs to any referenced metrics.
 *
 * @param params - The insights bookmark parameters containing sections to process
 * @returns A new copy of the insights bookmark parameters with IDs assigned to all metrics
 */
export function assignMetricIds(params: Draft<BookmarkTypes.InsightsBookmarkParams>, opts: {force?: boolean} = {}) {
  params.sections.show = params.sections.show.map((clause) => {
    clause._idx = opts.force || !clause._idx ? uuidv4() : clause._idx;

    if (isFormulaShowClause(clause) && clause.referencedMetrics) {
      clause.referencedMetrics = clause.referencedMetrics.map((metric) => {
        metric._idx = opts.force || !metric._idx ? uuidv4() : metric._idx;
        return metric;
      });
    }

    return clause;
  });

  return params;
}
